import {
  And,
  Desc,
  Eq,
  Expression,
  Relation,
  From,
  RankOver,
  RelationRef,
  Ty,
  Random,
  TC,
  Max,
  If,
  RowNumberOver,
} from '@cotera/era';
import { SYSTEM_TABLES } from '../runtime/system-tables/system-tables';
import _ from 'lodash';

export class UserDefinedDimensions {
  constructor(
    readonly config: {
      readonly entityName: string;
      readonly attributes: {
        readonly [name: string]: {
          readonly type: Ty.Shorthand;
          readonly key?: string;
        };
      };
    }
  ) {}

  get entityName(): string {
    return this.config.entityName;
  }

  join(
    rel: Relation,
    params: {
      readonly identifier: (t: RelationRef) => Expression;
    }
  ): Relation {
    const { identifier } = params;

    let BuiltRel = rel.select((t) => ({
      ...t.star(),
      __identifier: Expression.wrap(identifier(t)).cast('string'),
    }));

    let remainingCols: [
      string,
      { type: Ty.ExtendedAttributeType; key: string }
    ][] = Object.entries(this.config.attributes).map(
      ([name, { type, key }]) =>
        [name, { type: Ty.shorthandToTy(type), key: key ?? name }] as const
    );

    // Do the primative joins in a single cte
    const [inPrimative, rest] = _.partition(
      remainingCols,
      ([_name, { type }]) => TC.isPrimitive(type)
    );

    remainingCols = rest;

    if (inPrimative.length > 0) {
      const Partition = ((keys: string[]) =>
        SYSTEM_TABLES.user_defined_dimensions.where((t) =>
          And(
            t.attr('entity_id').eq(this.entityName),
            t.attr('key').oneOf(keys)
          )
        ))(inPrimative.map(([_name, { key }]) => key))
        .select((t) => ({
          ...t.star(),
          __rkk: RowNumberOver({
            partitionBy: [t.attr('identifier'), t.attr('key')],
            orderBy: [Desc(t.attr('timestamp'))],
          }),
        }))
        .where((t) => t.attr('__rkk').eq(1))
        .groupBy((t) => ({
          __identifier: t.attr('identifier'),
        }))
        .select((t) => {
          const cols = inPrimative.map(
            ([name, { type, key }]): [string, Expression] => {
              return [
                name,
                Max(
                  If(t.attr('key').eq(key), {
                    then: t.attr('value').cast(type),
                  })
                ).tag(...type.tags),
              ];
            }
          );

          return {
            ...Object.fromEntries(cols),
            ...t.pick('__identifier'),
          };
        });

      BuiltRel = BuiltRel.leftJoin(Partition, (base, prims) => ({
        on: Eq(base.attr('__identifier'), prims.attr('__identifier')),
        select: {
          ...prims.star(),
          ...base.star(),
        },
      }));
    }

    return remainingCols
      .reduce((rel: Relation, [name, { type, key }]): Relation => {
        return rel.leftJoin(
          this.mostRecentValuesForKey(key, type),
          (left, udds) => ({
            on: Eq(
              left.attr('__identifier').cast('string'),
              udds.attr('identifier')
            ),
            select: {
              ...left.star(),
              [name]: udds.attr('value').tag(...(type.tags ?? [])),
            },
          })
        );
      }, BuiltRel)
      .select((t) => t.except('__identifier'));
  }

  private mostRecentValuesForKey(
    key: string,
    type: Ty.ExtendedAttributeType
  ): Relation {
    return From(SYSTEM_TABLES.user_defined_dimensions)
      .where((t) =>
        And(Eq(t.attr('entity_id'), this.entityName), Eq(t.attr('key'), key))
      )
      .select((t) => ({
        ...t.star(),
        value: t.attr('value').cast(type),
        rkk: RankOver({
          partitionBy: [t.attr('identifier'), t.attr('key')],
          orderBy: [
            Desc(t.attr('timestamp')),
            // Break ties
            Desc(Random()),
          ],
        }),
      }))
      .where((t) => Eq(t.attr('rkk'), 1));
  }
}
